import { useEffect, useRef, useState } from 'react';

const FAMILY_IMAGE = 'https://public.readdy.ai/ai/img_res/10a76caa116ebf8f23dac14660f3f3be.jpg';

const benefits = [
  { icon: 'ri-eye-line', text: 'Comprendes con claridad cómo se mueve el dinero en tu hogar' },
  { icon: 'ri-bar-chart-line', text: 'Organizas ingresos y gastos de forma realista y sostenible' },
  { icon: 'ri-scissors-cut-line', text: 'Reduces gastos innecesarios sin afectar la calidad de vida' },
  { icon: 'ri-subtract-line', text: 'Creas un plan para eliminar deudas paso a paso' },
  { icon: 'ri-seedling-line', text: 'Construyes hábitos financieros saludables para toda la familia' },
  { icon: 'ri-graduation-cap-line', text: 'Enseñas a tus hijos educación financiera desde temprana edad' },
];

const learnings = [
  'Cómo evaluar ingresos, gastos y deudas familiares con claridad',
  'Cómo crear un presupuesto familiar que realmente funcione',
  'Cómo detectar y eliminar fugas de dinero',
  'Estrategias prácticas para reducir deudas',
  'Cómo construir un fondo de emergencia para proteger a tu familia',
  'Técnicas de ahorro aplicables a cualquier nivel de ingreso',
  'Cómo establecer metas financieras familiares realistas',
  'Cómo enseñar educación financiera a tus hijos',
];

export default function BeneficiosSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="beneficios" className="bg-stone-50 py-20 md:py-28 px-6 md:px-12" ref={ref}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-block text-emerald-700 text-xs font-bold tracking-widest uppercase mb-4">
            Lo que cambia cuando aplicas este programa
          </span>
          <h2
            className="text-3xl md:text-5xl font-bold text-stone-900 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            De la preocupación a{' '}
            <span className="text-emerald-700">dirigir tu economía</span>{' '}
            con inteligencia
          </h2>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {benefits.map((b, i) => (
            <div
              key={i}
              className={`bg-white border border-stone-100 rounded-xl p-6 flex items-start gap-4 transition-all duration-600 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-100 flex-shrink-0">
                <i className={`${b.icon} text-emerald-700 text-lg`} />
              </div>
              <p className="text-stone-700 text-sm leading-relaxed font-medium">{b.text}</p>
            </div>
          ))}
        </div>

        {/* What you'll learn */}
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          <div
            className={`flex-1 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
          >
            <span className="inline-block text-emerald-700 text-xs font-bold tracking-widest uppercase mb-4">
              Dentro del programa
            </span>
            <h3
              className="text-2xl md:text-3xl font-bold text-stone-900 mb-7 leading-tight"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Lo que aprenderás con este programa
            </h3>
            <div className="flex flex-col gap-3.5">
              {learnings.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-double-line text-emerald-600 text-base" />
                  </div>
                  <span className="text-stone-600 text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-stone-500 text-sm italic mt-6">
              No es teoría complicada. Es un plan práctico diseñado para reorganizar las finanzas familiares en 30 días.
            </p>
          </div>

          {/* Image */}
          <div
            className={`w-full lg:w-[460px] flex-shrink-0 transition-all duration-700 delay-400 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="w-full h-[360px] rounded-2xl overflow-hidden">
              <img
                src={FAMILY_IMAGE}
                alt="Familia feliz organizando sus finanzas"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
